// Gasbestellung beim Gaslieferanten (Kraiss & Friz) — ersetzt das Word-Formular
// "Schweißgas Bestellformular". Mengen eintragen → PDF im Formular-Layout oder
// Outlook-Entwurf mit PDF-Anhang. Jede Bestellung wird archiviert.
//
// 14.10.2026 (Hamdi/Jasmin):
//  • Ablauf Alen → Jasmin: „An Jasmin übergeben“ speichert, meldet (Popup) und Jasmin bekommt die Bestellung
//    beim Öffnen der Seite direkt vorgelegt. Offene Entwürfe öffnen sich automatisch (vorher: Seite leer →
//    „speichert nicht“).
//  • Flaschenkonto (300er-Vertrag): letzte Zählung + Lieferungen − abgeholtes Leergut, Inventur jederzeit.
//  • Leergut wird automatisch aus „davon leer“ vorgeschlagen.
//  • PDF/Outlook unverändert (Vorlage!) — Zusatzartikel landen im PDF in den freien Zeilen (lib/gasVorlage.ts).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, fehlerText, type GasArtikel, type GasBestandWert, type GasBestellung, type GasEinstellungen, type GasKonto, type GasPosition, type GasStatistik, type GasStatus } from '../lib/api';
import { gasBestellPdf, gasPdfVorladen, datumDE, type GasPdfDaten } from '../lib/gasPdf';
import { pdfEinstellungen } from '../lib/gasVorlage';
import { MAIL_SIGNATUR, oeffneOutlookEntwurf } from '../components/MailEntwurf';
import { gasBescheid } from '../lib/gasBescheid';
import { LEERGUT_ARTEN, leergutAus, leergutAusBestand, leergutFlaschen, leergutText, leergutZahlen, type LeergutEingabe } from '../lib/gasLeergut';

type Kopf = Omit<GasPdfDaten, 'positionen'>;

const heute = () => new Date().toISOString().slice(0, 10);
const schluessel = (a: GasArtikel) => `${a.artnr}|${a.artikel}`;
const hatMenge = (m?: string) => !!m && m.trim() !== '' && m.trim() !== '0';
const zahl = (v?: string) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const vorname = (n?: string | null) => (n ?? '').trim().split(/\s+/)[0] || '';
const zeitDE = (v?: string | null) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v ?? '');
  return m ? `${m[3]}.${m[2]}. ${m[4]}:${m[5]}` : datumDE(v ?? '');
};

export const GAS_STATUS_INFO: Record<GasStatus, { label: string; cls: string }> = {
  entwurf:    { label: 'Entwurf',    cls: 'bg-slate-100 text-slate-700' },
  uebergeben: { label: 'Bei Jasmin', cls: 'bg-amber-100 text-amber-800' },
  gesendet:   { label: 'Gesendet',   cls: 'bg-sky-100 text-sky-800' },
  geliefert:  { label: 'Geliefert',  cls: 'bg-emerald-100 text-emerald-800' },
};
function StatusChip({ s }: { s: GasStatus }) {
  const i = GAS_STATUS_INFO[s] ?? GAS_STATUS_INFO.entwurf;
  return <span className={`text-xs font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${i.cls}`}>{i.label}</span>;
}

// 300-Flaschen-Vertrag: Flaschen bei uns nach der Lieferung = Bestand + bestellt − Leergut
// Bestand = in dieser Bestellung gezählt, sonst Flaschenkonto (gleiche Rechnung wie api/src/routes/gasBestellungen.ts)
export function flaschenBilanz(e: GasEinstellungen, bestand: Record<string, GasBestandWert>, positionen: GasPosition[], leergutFl: number, basis?: number | null) {
  const faktor = new Map(e.artikel.map((a) => [schluessel(a), a.flaschen ?? 1]));
  const f = (k: string) => faktor.get(k) ?? 1;
  const summe = (feld: keyof GasBestandWert) => Object.entries(bestand).reduce((s, [k, v]) => s + zahl(v[feld]) * f(k), 0);
  const w1 = summe('w1'), w2 = summe('w2'), leerBeiUns = summe('leer');
  const gezaehlt = Object.values(bestand).some((v) => (v.w1 ?? '') !== '' || (v.w2 ?? '') !== '');
  const ausKonto = !gezaehlt && basis != null;
  const best = ausKonto ? basis! : w1 + w2;
  const bestellt = positionen.reduce((s, p) => s + zahl(p.menge) * f(schluessel(p)), 0);
  const leer = leergutFl;
  const nach = Math.round(best + bestellt - leer);
  const max = e.vertrag?.maxFlaschen ?? 300;
  return {
    bestand: Math.round(best), bestand_w1: Math.round(w1), bestand_w2: Math.round(w2), leer_bei_uns: Math.round(leerBeiUns),
    bestellt: Math.round(bestellt), leergut: leer, nach, max, erfasst: gezaehlt || ausKonto, aus_konto: ausKonto,
  };
}
type Bilanz = ReturnType<typeof flaschenBilanz>;

function leererKopf(e: GasEinstellungen | null): Kopf {
  const b = e?.besteller;
  return {
    bestelldatum: heute(), wunschtermin: '', bestelltext: '',
    ansprechpartner: b?.ansprechpartner ?? '', email: b?.email ?? '', telefon: b?.telefon ?? '',
    rechnungsadresse: b?.rechnungsadresse ?? '', lieferadresse: b?.lieferadresse ?? '', leergut: '',
  };
}

function dateiname(d: Kopf): string {
  return `Gasbestellung_Kraiss-Friz_${d.bestelldatum || heute()}.pdf`;
}

// Artikelgruppen für die Anzeige (nur Optik — PDF-Reihenfolge bleibt die der Einstellungen)
function gruppeVon(a: GasArtikel): 'flaschen' | 'buendel' | 'sonstiges' | 'zusatz' {
  if (a.zusatz) return 'zusatz';
  if ((a.flaschen ?? 1) === 0 || /treib/i.test(a.artikel)) return 'sonstiges';
  if (/b[üu]ndel/i.test(a.artikel) || a.groesse.trim() === '123') return 'buendel';
  return 'flaschen';
}
const GRUPPEN = [
  { k: 'flaschen', label: 'Flaschen' },
  { k: 'buendel', label: 'Bündel' },
  { k: 'sonstiges', label: 'Tank & Treibgas' },
  { k: 'zusatz', label: 'Zusatzartikel — im PDF als freie Zeile' },
] as const;

export function GasBestellung() {
  const einst = useApi(() => api.gas.einstellungen(), []);
  const liste = useApi(() => api.gas.list(), []);
  const kontoApi = useApi(() => api.gas.konto(), []);
  const e = einst.data?.data ?? null;
  const konto: GasKonto | null = kontoApi.data?.data ?? null;

  const [kopf, setKopf] = useState<Kopf>(leererKopf(null));
  const [mengen, setMengen] = useState<Record<string, string>>({});
  const [frei, setFrei] = useState<GasPosition[]>([]);
  const [bestand, setBestand] = useState<Record<string, GasBestandWert>>({});   // Flaschen bei uns je Artikel und Werk
  const [lg, setLg] = useState<LeergutEingabe>({});                     // Leergut je Art
  const [lgManuell, setLgManuell] = useState(false);                    // false = automatisch aus „davon leer“
  const [zaehlen, setZaehlen] = useState(false);
  const [trotzdem, setTrotzdem] = useState(false);                      // Ausnahme: ohne Bestand senden                        // vollständige Zählung (Werk 1/2) in dieser Bestellung
  const [id, setId] = useState<number | null>(null);
  const [status, setStatus] = useState<GasStatus>('entwurf');
  const [geladen, setGeladen] = useState<GasBestellung | null>(null);
  const [busy, setBusy] = useState('');
  const [meldung, setMeldung] = useState('');
  const [einstOffen, setEinstOffen] = useState(false);
  const [inventurOffen, setInventurOffen] = useState(false);
  const [lieferung, setLieferung] = useState<GasBestellung | null>(null);
  const [kopfOffen, setKopfOffen] = useState(false);
  const [ansicht, setAnsicht] = useState<'bestellen' | 'verlauf'>('bestellen');
  useEffect(() => { gasPdfVorladen(); }, []);   // PDF-Bausteine schon beim Öffnen laden

  // Standardwerte (Ansprechpartner, Lieferadresse …) einmalig aus den Einstellungen
  useEffect(() => { if (e && !id) { setKopf((k) => ({ ...leererKopf(e), ...pickGefuellt(k) })); setSauberMarke((n) => n + 1); } }, [e]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ungespeicherte Eingaben (v. a. Bestand) nicht versehentlich verlieren
  const schnappschuss = JSON.stringify([kopf, mengen, frei, bestand, lg]);
  const [sauber, setSauber] = useState(schnappschuss);
  const [sauberMarke, setSauberMarke] = useState(0);
  useEffect(() => { setSauber(schnappschuss); }, [sauberMarke]); // eslint-disable-line react-hooks/exhaustive-deps
  const ungespeichert = schnappschuss !== sauber;
  useEffect(() => {
    if (!ungespeichert) return;
    const warnen = (ev: BeforeUnloadEvent) => { ev.preventDefault(); ev.returnValue = ''; };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [ungespeichert]);

  const set = (p: Partial<Kopf>) => setKopf((k) => ({ ...k, ...p }));
  // Ohne Flaschenkonto muss gezählt werden; sonst nur, wenn jemand bewusst vollständig zählt.
  // „davon leer“ ist immer sichtbar → daraus wird das Leergut vorgeschlagen.
  const zaehlModus = zaehlen || konto?.aktuell == null;

  const positionen: GasPosition[] = useMemo(() => {
    if (!e) return [];
    const ausListe = e.artikel
      .map((a) => ({ ...a, menge: (mengen[schluessel(a)] ?? '').trim() }))
      .filter((p) => hatMenge(p.menge))
      .map(({ zusatz: _z, ...p }) => p);
    const freieZeilen = frei
      .filter((p) => p.artikel.trim() || p.artnr.trim())
      .map((p) => ({ ...p, frei: true, menge: p.menge.trim() }));
    return [...ausListe, ...freieZeilen];
  }, [e, mengen, frei]);
  const anzahl = positionen.filter((p) => hatMenge(p.menge)).length;

  // eingetragener Bestand (Liste + freie Zeilen), leere Felder zählen nicht als „erfasst"
  const bestandMap = useMemo(() => {
    const o: Record<string, GasBestandWert> = {};
    const felder = zaehlModus ? (['w1', 'w2', 'leer'] as const) : (['leer'] as const);
    const nurGefuellt = (v: GasBestandWert = {}) => {
      const w: GasBestandWert = {};
      felder.forEach((f) => { const t = (v[f] ?? '').trim(); if (t !== '') w[f] = t; });
      return Object.keys(w).length ? w : null;
    };
    Object.entries(bestand).forEach(([k, v]) => { const w = nurGefuellt(v); if (w) o[k] = w; });
    frei.forEach((p) => { const w = nurGefuellt(p.bestand); if (w && (p.artikel.trim() || p.artnr.trim())) o[schluessel(p)] = w; });
    return o;
  }, [bestand, frei, zaehlModus]);

  // Leergut automatisch aus „davon leer“, solange niemand es von Hand geändert hat
  const lgAuto = useMemo(() => (e ? leergutAusBestand(e.artikel, bestandMap) : {}), [e, bestandMap]);
  const lgAutoText = JSON.stringify(lgAuto);
  useEffect(() => {
    if (lgManuell || lgAutoText === '{}') return;
    setLg(lgAuto);
  }, [lgAutoText, lgManuell]); // eslint-disable-line react-hooks/exhaustive-deps
  const lgIstAuto = !lgManuell && lgAutoText !== '{}';

  const bil = e ? flaschenBilanz(e, bestandMap, positionen, leergutFlaschen(lg), konto?.aktuell) : null;
  const lgText = leergutText(lg);
  const zuViel = !!bil && bil.nach > bil.max;
  const ohneBestand = !!bil && !bil.erfasst && trotzdem;   // Ausnahme greift nur, solange der Bestand fehlt
  const gesperrt = !bil || zuViel || (!bil.erfasst && !trotzdem);
  // letzte Zählung → grauer Hinweis in den Bestandsfeldern
  const letzteBestand = konto?.inventur?.bestand ?? liste.data?.data?.find((b) => b.bestand && Object.keys(b.bestand).length)?.bestand ?? {};

  const laden = (b: GasBestellung, alsNeu: boolean, still = false) => {
    if (!e) return;
    if (!still && ungespeichert && !confirm('Ungespeicherte Eingaben verwerfen?')) return;
    const bekannt = new Set(e.artikel.map(schluessel));
    const m: Record<string, string> = {};
    const f: GasPosition[] = [];
    const bst = b.bestand ?? {};
    b.positionen.forEach((p) => {
      if (!p.frei && bekannt.has(schluessel(p))) m[schluessel(p)] = p.menge;
      else f.push({ ...p, frei: true, bestand: alsNeu ? {} : (bst[schluessel(p)] ?? {}) });
    });
    setMengen(m);
    const lgNeu = alsNeu ? {} : leergutAus(b.leergut_detail, b.leergut);
    setLg(lgNeu);
    setLgManuell(!alsNeu);   // gespeicherte Bestellung: Leergut so lassen, wie es gespeichert wurde
    setZaehlen(!alsNeu && Object.values(bst).some((v) => (v.w1 ?? '') !== '' || (v.w2 ?? '') !== ''));
    // Wiederholen: Bestand neu zählen lassen (letzter Wert steht als grauer Hinweis im Feld)
    setBestand(alsNeu ? {} : Object.fromEntries(Object.entries(bst).filter(([k]) => bekannt.has(k))));
    setFrei(f);
    setKopf({
      bestelldatum: alsNeu ? heute() : (b.bestelldatum || heute()),
      wunschtermin: alsNeu ? '' : (b.wunschtermin || ''),
      bestelltext: alsNeu ? '' : (b.bestelltext || ''),
      ansprechpartner: b.ansprechpartner || '', email: b.email || '', telefon: b.telefon || '',
      rechnungsadresse: b.rechnungsadresse || '', lieferadresse: b.lieferadresse || '',
      leergut: '',
    });
    setId(alsNeu ? null : b.id);
    setStatus(alsNeu ? 'entwurf' : b.status);
    setGeladen(alsNeu ? null : b);
    if (!alsNeu) setSauberMarke((n) => n + 1);   // geöffnete Bestellung = gespeicherter Stand
    if (!still) setMeldung(alsNeu ? 'Bestellung vom ' + datumDE(b.bestelldatum) + ' übernommen — Mengen prüfen.' : 'Bestellung geöffnet.');
    setAnsicht('bestellen');
    if (!still) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Offene Bestellung (an Jasmin übergeben oder Entwurf) beim Öffnen der Seite direkt vorlegen
  const offene = (liste.data?.data ?? []).filter((b) => b.status === 'uebergeben' || b.status === 'entwurf')
    .sort((x, y) => (x.status === y.status ? 0 : x.status === 'uebergeben' ? -1 : 1));
  const autoGeladen = useRef(false);
  useEffect(() => {
    if (autoGeladen.current || !e || !liste.data) return;
    autoGeladen.current = true;
    const b = offene[0];
    // nur, solange niemand etwas eingetragen hat (Standardwerte im Kopf zählen nicht als Eingabe)
    const nochLeer = !id && !Object.values(mengen).some(hatMenge) && !frei.length && !Object.keys(bestand).length;
    if (b && nochLeer) laden(b, false, true);
  }, [e, liste.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const neu = () => {
    if (ungespeichert && !confirm('Ungespeicherte Eingaben verwerfen?')) return;
    setKopf(leererKopf(e)); setMengen({}); setFrei([]); setBestand({}); setLg({}); setLgManuell(false); setZaehlen(false); setTrotzdem(false);
    setId(null); setStatus('entwurf'); setGeladen(null); setMeldung('');
    setSauberMarke((n) => n + 1);
  };

  // Speichern (neu oder Änderung); liefert die gespeicherte Bestellung
  const speichern = async (neuerStatus?: 'gesendet' | 'uebergeben'): Promise<GasBestellung | null> => {
    if (!anzahl) { alert('Bitte mindestens eine Bestellmenge eintragen.'); return null; }
    const body: Partial<GasBestellung> = { ...kopf, leergut: lgText, leergut_detail: leergutZahlen(lg), positionen, bestand: bestandMap, ...(neuerStatus ? { status: neuerStatus } : {}),
      ...(neuerStatus === 'gesendet' && ohneBestand ? { ohne_bestand: true } : {}) };
    const r = id ? await api.gas.update(id, body) : await api.gas.create(body);
    setId(r.data.id);
    setStatus(r.data.status);
    setGeladen(r.data);
    setSauber(schnappschuss);
    liste.reload();
    kontoApi.reload();
    return r.data;
  };

  const mitBusy = (name: string, f: () => Promise<void>) => async () => {
    setBusy(name);
    try { await f(); } catch (err) { alert(fehlerText(err)); }
    setBusy('');
  };

  const pdfErzeugen = async (daten: GasPdfDaten) => gasBestellPdf(pdfEinstellungen(e!), daten);

  const onSpeichern = mitBusy('speichern', async () => {
    if (await speichern()) setMeldung('✓ Gespeichert.');
  });
  const grenzeOk = () => {
    if (ohneBestand) return confirm('Ohne Flaschenbestand senden?\n\nDie 300-Flaschen-Prüfung entfällt für diese Bestellung. Bitte so bald wie möglich eine Inventur machen.');
    if (!bil?.erfasst) { alert(`Bitte zuerst den aktuellen Flaschenbestand eintragen oder eine Inventur machen (Vertrag max. ${bil?.max ?? 300} Flaschen).`); return false; }
    if (zuViel) { alert(`Nach der Lieferung wären ${bil.nach} Flaschen bei uns – Vertrag erlaubt max. ${bil.max}.\nBitte ${bil.nach - bil.max} Flaschen mehr Leergut zurückgeben oder weniger bestellen.`); return false; }
    return true;
  };
  const onPdf = mitBusy('pdf', async () => {
    if (!grenzeOk()) return;
    if (!(await speichern())) return;
    const pdf = await pdfErzeugen({ ...kopf, leergut: lgText, positionen });
    pdf.save(dateiname(kopf));
    setMeldung('✓ PDF erstellt und Bestellung gespeichert.');
  });
  const onOutlook = mitBusy('outlook', async () => {
    if (!grenzeOk()) return;
    if (!(await speichern('gesendet'))) return;
    const lf = e!.lieferant;
    const pdf = await pdfErzeugen({ ...kopf, leergut: lgText, positionen });
    const b64 = (pdf.output('datauristring') as string).split('base64,')[1] ?? '';
    const zeilen = positionen.filter((p) => hatMenge(p.menge))
      .map((p) => `   ${p.menge} × ${p.artikel}${p.artnr ? ` (Art.-Nr. ${p.artnr}${p.groesse ? `, ${p.groesse}` : ''})` : ''}`);
    const text = [
      'Sehr geehrte Damen und Herren,',
      '',
      `anbei erhalten Sie unsere Gasbestellung (Kd-Nr. ${lf.kundennr}) als PDF:`,
      '',
      ...zeilen,
      '',
      `Wunschliefertermin: ${datumDE(kopf.wunschtermin) || 'nächster Liefertermin'}`,
      kopf.lieferadresse ? `Lieferadresse: ${kopf.lieferadresse.replace(/\n/g, ', ')}` : '',
      kopf.bestelltext ? `Bestelltext/-Nr.: ${kopf.bestelltext}` : '',
      lgText ? `Leergutrücknahme: ${lgText}` : '',
      '',
      'Für eine kurze Bestätigung wären wir Ihnen dankbar.',
      '',
      MAIL_SIGNATUR,
    ].filter((z, i, a) => z !== '' || a[i - 1] !== '').join('\n');
    oeffneOutlookEntwurf(
      { an: lf.email, betreff: `Gasbestellung Sickinger Kd-Nr. ${lf.kundennr} – ${datumDE(kopf.bestelldatum)}`, text },
      [{ name: dateiname(kopf), mime: 'application/pdf', b64 }],
    );
    setMeldung('✓ Outlook-Entwurf mit PDF erstellt (Datei doppelklicken) — Bestellung als „gesendet" markiert.');
  });
  // An Jasmin übergeben: Bestellung speichern (Status „Bei Jasmin“) + Meldung an den Einkauf (Popup/Badge/NEU)
  const schonUebergeben = status === 'uebergeben' && !ungespeichert;
  const onUebergeben = mitBusy('bescheid', async () => {
    if (schonUebergeben && !confirm('Jasmin hat diese Bestellung schon bekommen. Nochmal melden?')) return;
    const b = await speichern(status === 'gesendet' || status === 'geliefert' ? undefined : 'uebergeben');
    if (!b) return;
    const t = gasBescheid(positionen, kopf, lgText, bil);
    await api.einkaufAufgaben.create({ titel: t.titel, notiz: t.notiz, typ: 'bestellung' });
    setMeldung('✓ An Jasmin übergeben — sie bekommt ein Popup und findet die Bestellung hier fertig vorbereitet.');
  });
  const pdfAusListe = (b: GasBestellung) => mitBusy('liste', async () => {
    const pdf = await pdfErzeugen(b);
    pdf.save(dateiname(b));
  })();
  const loeschen = async (b: GasBestellung) => {
    if (!confirm(`Bestellung vom ${datumDE(b.bestelldatum)} löschen?`)) return;
    try { await api.gas.remove(b.id); if (id === b.id) neu(); liste.reload(); kontoApi.reload(); }
    catch (err) { alert(fehlerText(err)); }
  };
  const lieferungZuruecknehmen = async (b: GasBestellung) => {
    if (!confirm(`Lieferung zu der Bestellung vom ${datumDE(b.bestelldatum)} zurücknehmen? (Flaschenkonto wird korrigiert)`)) return;
    try { await api.gas.update(b.id, { status: 'gesendet' }); liste.reload(); kontoApi.reload(); if (id === b.id) setStatus('gesendet'); }
    catch (err) { alert(fehlerText(err)); }
  };

  if (einst.loading && !e) return <div className="p-8 text-slate-500">Lade …</div>;
  if (einst.error || !e) return <div className="p-8 text-red-600">Fehler beim Laden: {einst.error ? fehlerText(einst.error) : ''}</div>;

  const lf = e.lieferant;
  const letzte = liste.data?.data?.find((b) => b.status === 'gesendet' || b.status === 'geliefert') ?? liste.data?.data?.[0];
  const weitereOffene = offene.filter((b) => b.id !== id);
  const kopfZusammenfassung = [kopf.ansprechpartner, kopf.lieferadresse.replace(/\n/g, ', '), kopf.wunschtermin ? `Wunschtermin ${/^\d{4}-\d{2}-\d{2}$/.test(kopf.wunschtermin) ? datumDE(kopf.wunschtermin) : kopf.wunschtermin}` : 'Wunschtermin: nächster Liefertermin', kopf.bestelltext ? `Bestelltext ${kopf.bestelltext}` : ''].filter(Boolean).join(' · ');

  // Artikel nach Gruppen (Anzeige)
  const gruppiert = GRUPPEN.map((g) => ({ ...g, artikel: e.artikel.filter((a) => gruppeVon(a) === g.k) })).filter((g) => g.artikel.length);

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <header className="mb-5 flex flex-wrap items-end gap-3">
        <div className="mr-auto min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
          <h1 className="text-2xl font-bold text-slate-900">Gasbestellung</h1>
          <div className="text-sm text-slate-500 truncate">
            <b className="text-slate-700">{lf.name}</b> · {lf.abteilung} · Kd-Nr. <b className="text-slate-700">{lf.kundennr}</b> · {lf.email} · Tel. {lf.telefon}
          </div>
        </div>
        {letzte && !id && (
          <button className="btn-secondary" onClick={() => laden(letzte, true)} title="Mengen und Adressen der letzten Bestellung übernehmen">
            ↺ Letzte Bestellung übernehmen
          </button>
        )}
        {id && <button className="btn-secondary" onClick={neu}>＋ Neue Bestellung</button>}
        <button className="btn-secondary" onClick={() => setEinstOffen(true)} title="Lieferant, Kunden-Nr., Artikelliste">⚙ Artikel &amp; Lieferant</button>
      </header>

      <KontoKarte konto={konto} laedt={kontoApi.loading && !konto} onInventur={() => setInventurOffen(true)} />

      {/* Offene Bestellung: wer hat sie vorbereitet? */}
      {id && geladen && (status === 'uebergeben' || status === 'entwurf') && (
        <div data-testid="gas-offen-banner"
             className={`mb-4 rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 text-sm ${status === 'uebergeben' ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-slate-50 border-slate-300 text-slate-700'}`}>
          <span className="text-lg" aria-hidden>{status === 'uebergeben' ? '📨' : '📝'}</span>
          <span className="mr-auto">
            {status === 'uebergeben'
              ? <><b>Von {vorname(geladen.uebergeben_von) || vorname(geladen.erstellt_von) || 'Kollegen'} vorbereitet</b>{geladen.uebergeben_am ? ` am ${zeitDE(geladen.uebergeben_am)}` : ''} — bitte prüfen und an {lf.name} senden.</>
              : <><b>Offener Entwurf</b> vom {datumDE(geladen.bestelldatum)}{geladen.erstellt_von ? ` (${vorname(geladen.erstellt_von)})` : ''} — weiter bearbeiten oder übergeben.</>}
            {weitereOffene.length > 0 && <button className="ml-2 underline" onClick={() => setAnsicht('verlauf')}>+{weitereOffene.length} weitere offen</button>}
          </span>
          <button className="btn-secondary !py-1" onClick={neu}>Stattdessen neue Bestellung</button>
        </div>
      )}

      <div className="flex gap-1 border border-slate-200 bg-white rounded-lg p-1 mb-4 w-fit" role="tablist">
        {([['bestellen', 'Bestellung'], ['verlauf', `Verlauf & Auswertung${liste.data?.data?.length ? ` (${liste.data.data.length})` : ''}`]] as const).map(([k, t]) => (
          <button key={k} role="tab" aria-selected={ansicht === k} onClick={() => setAnsicht(k)}
                  className={`px-4 py-1.5 rounded text-sm font-medium ${ansicht === k ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t}
          </button>
        ))}
      </div>

      {meldung && ansicht === 'bestellen' && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{meldung}</div>}

      {ansicht === 'bestellen' && (
      <div className="grid lg:grid-cols-[minmax(0,1fr)_330px] gap-5 items-start">
        <div className="space-y-4 min-w-0">

          {/* ① Lieferdaten */}
          <section className="bg-white border border-slate-200 rounded-xl">
            <button type="button" onClick={() => setKopfOffen((o) => !o)} aria-expanded={kopfOffen} data-testid="gas-kopf-toggle"
                    className="w-full flex items-center gap-3 px-5 py-3 text-left">
              <Nr n={1} />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-800">Lieferdaten</span>
                {!kopfOffen && <span className="block text-xs text-slate-500 truncate">{datumDE(kopf.bestelldatum)} · {kopfZusammenfassung}</span>}
              </span>
              <span className="text-xs text-brand font-medium">{kopfOffen ? 'zuklappen ▴' : 'ändern ▾'}</span>
            </button>
            {kopfOffen && (
              <div className="px-5 pb-5">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Feld label="Ansprechpartner"><input className="input w-full" value={kopf.ansprechpartner} onChange={(x) => set({ ansprechpartner: x.target.value })} /></Feld>
                  <Feld label="E-Mail"><input className="input w-full" value={kopf.email} onChange={(x) => set({ email: x.target.value })} /></Feld>
                  <Feld label="Telefon-Nr"><input className="input w-full" value={kopf.telefon} onChange={(x) => set({ telefon: x.target.value })} /></Feld>
                  <Feld label="Bestelldatum"><input type="date" className="input w-full" value={kopf.bestelldatum} onChange={(x) => set({ bestelldatum: x.target.value })} /></Feld>
                  <Feld label="Wunschliefertermin (Datum oder Text)">
                    <div className="flex gap-1.5">
                      <input className="input w-full" list="dl-wunschtermin" placeholder="z. B. sehr eilig"
                             value={/^\d{4}-\d{2}-\d{2}$/.test(kopf.wunschtermin) ? datumDE(kopf.wunschtermin) : kopf.wunschtermin}
                             onChange={(x) => set({ wunschtermin: x.target.value })} />
                      <input type="date" className="input w-11 px-1 shrink-0" title="Datum wählen" aria-label="Datum wählen"
                             value={/^\d{4}-\d{2}-\d{2}$/.test(kopf.wunschtermin) ? kopf.wunschtermin : ''}
                             onChange={(x) => set({ wunschtermin: x.target.value })} />
                    </div>
                    <datalist id="dl-wunschtermin">
                      <option value="sehr eilig" /><option value="nächster Liefertermin" /><option value="nach Absprache" />
                    </datalist>
                  </Feld>
                  <Feld label="Bestelltext/-Nr."><input className="input w-full" value={kopf.bestelltext} onChange={(x) => set({ bestelltext: x.target.value })} /></Feld>
                  <Feld label="Rechnungsadresse (leer = wie hinterlegt)">
                    <textarea className="input w-full" rows={2} value={kopf.rechnungsadresse} onChange={(x) => set({ rechnungsadresse: x.target.value })} />
                  </Feld>
                  <Feld label="Lieferadresse">
                    <textarea className="input w-full" rows={2} value={kopf.lieferadresse} onChange={(x) => set({ lieferadresse: x.target.value })} />
                  </Feld>
                </div>
                <div className="text-xs text-slate-500 mt-2">Lieferturnus: {lf.lieferturnus}</div>
              </div>
            )}
          </section>

          {/* ② Artikel: Bestand + Bestellmenge */}
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
              <Nr n={2} />
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">Menge bestellen &amp; leere Flaschen</div>
                <div className="text-xs text-slate-500">
                  „Leer“ eintragen → das Leergut unten füllt sich automatisch.{' '}
                  {konto?.aktuell != null
                    ? <>Bestand kommt aus dem Flaschenkonto ({konto.aktuell} Flaschen).</>
                    : <><b>Noch keine Zählung im Flaschenkonto</b> — bitte Werk 1 / Werk 2 vollständig zählen.</>}
                  {' '}Grau = letzte Zählung.
                </div>
              </div>
              {konto?.aktuell != null && (
                <label className="ml-auto flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap cursor-pointer" title="Alle Flaschen in Werk 1 und Werk 2 zählen — ersetzt den Stand im Flaschenkonto">
                  <input type="checkbox" data-testid="gas-zaehlen" checked={zaehlen} onChange={(x) => setZaehlen(x.target.checked)} />
                  Jetzt vollständig zählen
                </label>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2">Artikel</th>
                    {zaehlModus && <th className="text-center px-1 py-2 w-[70px] border-l border-slate-200" title="Flaschen (voll + leer) in Werk 1">Werk 1</th>}
                    {zaehlModus && <th className="text-center px-1 py-2 w-[70px]" title="Flaschen (voll + leer) in Werk 2">Werk 2</th>}
                    <th className={`text-center px-1 py-2 w-[70px] ${zaehlModus ? '' : 'border-l border-slate-200'}`} title="Leere Flaschen (beide Werke) → Leergut">{zaehlModus ? 'davon leer' : 'leer'}</th>
                    <th className="text-right px-4 py-2 w-40 border-l border-slate-200">Bestellen</th>
                  </tr>
                </thead>
                <tbody>
                  {gruppiert.map((g) => (
                    <GruppeZeilen key={g.k} label={g.label}>
                      {g.artikel.map((a) => {
                        const k = schluessel(a);
                        const m = mengen[k] ?? '';
                        return (
                          <tr key={k} data-testid={`gas-zeile-${a.artikel}`} className={`border-b border-slate-100 ${hatMenge(m) ? 'bg-amber-50/70' : ''}`}>
                            <td className="px-4 py-1.5">
                              <div className={hatMenge(m) ? 'font-semibold text-slate-900' : 'text-slate-800'}>{a.artikel}</div>
                              <div className="text-[11px] text-slate-400 font-mono">{a.artnr || 'Art.-Nr. fehlt (⚙)'}{a.groesse ? ` · ${a.groesse}` : ''}</div>
                            </td>
                            {(a.flaschen ?? 1) === 0
                              ? <td colSpan={zaehlModus ? 3 : 1} className="px-3 py-1 text-center text-xs text-slate-400 border-l border-slate-100" title="Zählt nicht zum Flaschen-Vertrag">—</td>
                              : <BestandZellen name={a.artikel} wert={bestand[k] ?? {}} letzte={letzteBestand[k]} nurLeer={!zaehlModus}
                                               onChange={(w) => setBestand((b) => ({ ...b, [k]: w }))} />}
                            <td className="px-4 py-1 border-l border-slate-100">
                              <Menge wert={m} onChange={(v) => setMengen((x) => ({ ...x, [k]: v }))} />
                            </td>
                          </tr>
                        );
                      })}
                    </GruppeZeilen>
                  ))}
                  {frei.length > 0 && <tr><td colSpan={zaehlModus ? 5 : 3} className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Freie Zeilen</td></tr>}
                  {frei.map((p, i) => (
                    <tr key={'f' + i} className={`border-b border-slate-100 ${hatMenge(p.menge) ? 'bg-amber-50/70' : ''}`}>
                      <td className="px-3 py-1">
                        <div className="flex gap-1">
                          <input className="input w-24 font-mono" placeholder="Art.-Nr" value={p.artnr}
                            onChange={(x) => setFrei(frei.map((y, j) => (j === i ? { ...y, artnr: x.target.value } : y)))} />
                          <input className="input flex-1 min-w-0" placeholder="Artikel (frei)" value={p.artikel}
                            onChange={(x) => setFrei(frei.map((y, j) => (j === i ? { ...y, artikel: x.target.value } : y)))} />
                          <input className="input w-20" placeholder="Größe" value={p.groesse}
                            onChange={(x) => setFrei(frei.map((y, j) => (j === i ? { ...y, groesse: x.target.value } : y)))} />
                        </div>
                      </td>
                      <BestandZellen name={p.artikel || 'freie Zeile'} wert={p.bestand ?? {}} nurLeer={!zaehlModus}
                                     onChange={(w) => setFrei(frei.map((y, j) => (j === i ? { ...y, bestand: w } : y)))} />
                      <td className="px-4 py-1 border-l border-slate-100">
                        <div className="flex items-center gap-1 justify-end">
                          <Menge wert={p.menge} onChange={(v) => setFrei(frei.map((y, j) => (j === i ? { ...y, menge: v } : y)))} />
                          <button className="text-slate-300 hover:text-red-500 px-1" title="Zeile entfernen" onClick={() => setFrei(frei.filter((_, j) => j !== i))}>×</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={zaehlModus ? 5 : 3} className="px-4 py-2">
                      <button className="text-sm text-brand hover:underline" onClick={() => setFrei([...frei, { artnr: '', artikel: '', groesse: '', menge: '', frei: true }])}>
                        ＋ freie Zeile (Artikel nicht in der Liste)
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ③ Leergut */}
          <section className="bg-white border border-slate-200 rounded-xl px-5 py-4" data-testid="gas-leergut">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Nr n={3} />
              <div className="mr-auto">
                <div className="font-semibold text-slate-800">Leergutrücknahme</div>
                <div className="text-xs text-slate-500">Schweißgas, Bündel und Treibgas zählen für den Vertrag, die Gitterbox nicht.</div>
              </div>
              {lgIstAuto && <span data-testid="gas-leergut-auto" className="text-xs font-semibold rounded-full bg-sky-100 text-sky-800 px-2 py-0.5">automatisch aus „davon leer“</span>}
              {lgManuell && lgAutoText !== '{}' && (
                <button type="button" data-testid="gas-leergut-auto-an" className="text-xs text-brand hover:underline" onClick={() => setLgManuell(false)}>↺ wieder automatisch</button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {LEERGUT_ARTEN.map((a) => (
                <label key={a.k} className="block" title={a.zaehlt ? 'zählt für den Flaschen-Vertrag' : 'Transportgestell – zählt nicht als Flasche'}>
                  <span className="block text-xs text-slate-500 mb-1">{a.label}{!a.zaehlt && <span className="text-slate-400"> (zählt nicht)</span>}</span>
                  <input className="input w-full text-right font-semibold" inputMode="numeric" aria-label={`Leergut ${a.label}`} value={lg[a.k] ?? ''}
                         onChange={(x) => { setLgManuell(true); setLg((v) => ({ ...v, [a.k]: x.target.value.replace(/[^\d]/g, '') })); }} />
                </label>
              ))}
            </div>
            {lgText && <div className="text-xs text-slate-500 mt-2">Im Formular: <b className="text-slate-700">{lgText}</b></div>}
          </section>
        </div>

        {/* ④ Zusammenfassung + Aktionen (bleibt beim Scrollen sichtbar) */}
        <aside className="lg:sticky lg:top-4 space-y-3" data-testid="gas-zusammenfassung">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Nr n={4} />
              <span className="font-semibold text-slate-800 mr-auto">Prüfen &amp; senden</span>
              <StatusChip s={status} />
            </div>
            <div className="text-xs text-slate-500 mb-3">
              Bestellung {id ? `#${id}` : '(neu)'} · {datumDE(kopf.bestelldatum)}
              {ungespeichert && <span className="ml-2 text-amber-700 font-semibold">● nicht gespeichert</span>}
            </div>
            {anzahl === 0
              ? <div className="text-sm text-slate-400 py-2">Noch keine Menge eingetragen.</div>
              : (
                <ul className="text-sm space-y-1 mb-2" data-testid="gas-positionen">
                  {positionen.filter((p) => hatMenge(p.menge)).map((p) => (
                    <li key={schluessel(p)} className="flex gap-2"><b className="tabular-nums w-8 text-right shrink-0">{p.menge}×</b><span className="min-w-0 truncate" title={p.artikel}>{p.artikel}</span></li>
                  ))}
                </ul>
              )}
            {lgText && <div className="text-xs text-slate-600 border-t border-slate-100 pt-2">Leergut: <b>{lgText}</b></div>}
          </div>

          {bil && <BilanzKarte bil={bil} konto={konto} trotzdem={trotzdem} onTrotzdem={setTrotzdem}
                    onLeergut={(mehr) => { setLgManuell(true); setLg((v) => ({ ...v, schweissgas: String((parseInt(v.schweissgas ?? '') || 0) + mehr) })); }} />}

          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
            <button className="bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50 w-full"
                    disabled={!!busy || !anzahl || gesperrt} onClick={onOutlook} title={`Outlook-Entwurf an ${lf.email} mit PDF im Anhang`}>
              {busy === 'outlook' ? 'Erzeuge…' : '📧 Outlook-Entwurf mit PDF'}
            </button>
            <button className={`w-full rounded-lg px-4 py-2 text-sm font-semibold border disabled:opacity-50 ${schonUebergeben ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-amber-400 text-amber-800 hover:bg-amber-50'}`}
                    disabled={!!busy || !anzahl} onClick={onUebergeben} data-testid="gas-bescheid"
                    title="Speichert die Bestellung als „Bei Jasmin“ und meldet sie automatisch (Popup beim Einkauf)">
              {busy === 'bescheid' ? 'Übergebe…' : schonUebergeben ? '✓ Bei Jasmin' : '📨 An Jasmin übergeben'}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button className="btn-secondary" disabled={!!busy} onClick={onSpeichern}>{busy === 'speichern' ? 'Speichern…' : '💾 Speichern'}</button>
              <button className="btn-secondary" disabled={!!busy || !anzahl || gesperrt} onClick={onPdf} title="PDF im Formular-Layout herunterladen">{busy === 'pdf' ? 'PDF…' : '⬇ PDF'}</button>
            </div>
            {status === 'gesendet' && geladen && (
              <button className="w-full rounded-lg px-4 py-2 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700" data-testid="gas-geliefert"
                      onClick={() => setLieferung(geladen)}>✓ Lieferung bestätigen</button>
            )}
          </div>
        </aside>
      </div>
      )}

      {ansicht === 'verlauf' && <Auswertung e={e} />}

      {/* Verlauf */}
      {ansicht === 'verlauf' && <section>
        <h2 className="font-semibold text-slate-800 mb-2">Alle Bestellungen</h2>
        {liste.loading && !liste.data ? <div className="text-sm text-slate-500">Lade …</div> :
          !(liste.data?.data?.length) ? <div className="text-sm text-slate-500">Noch keine Bestellungen gespeichert.</div> : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Bestellt</th>
                  <th className="text-left px-3 py-2">Positionen</th>
                  <th className="text-left px-3 py-2">Wunschtermin</th>
                  <th className="text-right px-3 py-2" title="Flaschen bei uns nach dieser Lieferung">Flaschen danach</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">von</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.data.data.map((b) => (
                  <tr key={b.id} data-testid="gas-verlauf-zeile" className={`border-b border-slate-100 ${b.id === id ? 'bg-sky-50' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{datumDE(b.bestelldatum)}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {b.positionen.filter((p) => hatMenge(p.menge)).map((p) => `${p.menge}× ${p.artikel}`).join(' · ')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{datumDE(b.wunschtermin) || '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${(b.flaschen_nach ?? 0) > (e.vertrag?.maxFlaschen ?? 300) ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                      {b.flaschen_nach != null ? `${b.flaschen_nach} / ${e.vertrag?.maxFlaschen ?? 300}` : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusChip s={b.status} />
                      {b.status === 'geliefert' && b.geliefert_am && <div className="text-[11px] text-slate-400 mt-0.5">am {datumDE(b.geliefert_am)}</div>}
                      {!!b.ohne_bestand && <div className="text-[11px] text-amber-700 font-semibold mt-0.5" title="300er-Prüfung entfiel">⚠ ohne Bestand</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{b.erstellt_von ?? ''}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {b.status === 'gesendet' && <button className="text-emerald-700 font-semibold hover:underline mr-3" onClick={() => setLieferung(b)}>✓ Geliefert</button>}
                      {b.status === 'geliefert' && <button className="text-slate-500 hover:underline mr-3" onClick={() => void lieferungZuruecknehmen(b)} title="Lieferung zurücknehmen">↩</button>}
                      <button className="text-brand hover:underline mr-3" onClick={() => laden(b, false)}>Öffnen</button>
                      <button className="text-brand hover:underline mr-3" onClick={() => laden(b, true)} title="Als Vorlage für eine neue Bestellung">Wiederholen</button>
                      <button className="text-brand hover:underline mr-3" onClick={() => pdfAusListe(b)}>PDF</button>
                      <button className="text-slate-400 hover:text-red-600" onClick={() => loeschen(b)} title="Löschen">🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>}

      {einstOffen && (
        <EinstellungenModal wert={e} onClose={() => setEinstOffen(false)}
          onSaved={() => { setEinstOffen(false); einst.reload(); }} />
      )}
      {inventurOffen && (
        <InventurModal e={e} konto={konto} onClose={() => setInventurOffen(false)}
          onSaved={() => { setInventurOffen(false); kontoApi.reload(); setMeldung('✓ Inventur gespeichert — Flaschenkonto aktualisiert.'); }} />
      )}
      {lieferung && (
        <LieferungModal b={lieferung} onClose={() => setLieferung(null)}
          onSaved={(neu) => { setLieferung(null); liste.reload(); kontoApi.reload(); if (id === neu.id) { setStatus(neu.status); setGeladen(neu); } setMeldung('✓ Lieferung bestätigt — Flaschenkonto aktualisiert.'); }} />
      )}
    </div>
  );
}

function Nr({ n }: { n: number }) {
  return <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center shrink-0">{n}</span>;
}
function GruppeZeilen({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <tr><td colSpan={9} className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</td></tr>
      {children}
    </>
  );
}

// ── Flaschenkonto (oben auf der Seite) ──
function KontoKarte({ konto, laedt, onInventur }: { konto: GasKonto | null; laedt: boolean; onInventur: () => void }) {
  if (laedt) return <div className="mb-4 h-24 rounded-xl bg-white border border-slate-200 animate-pulse" />;
  const max = konto?.max ?? 300;
  const inv = konto?.inventur;
  if (!konto || konto.aktuell == null || !inv) {
    return (
      <section data-testid="gas-konto" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 flex flex-wrap items-center gap-3">
        <span className="text-2xl" aria-hidden>🧮</span>
        <div className="mr-auto text-sm text-amber-900">
          <div className="font-semibold">Flaschenkonto: noch keine Zählung</div>
          Einmal alle Flaschen zählen (Werk 1, Werk 2, davon leer) — danach rechnet das Konto mit jeder Lieferung selbst weiter. Vertrag: max. {max} Flaschen.
        </div>
        <button className="bg-amber-500 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:opacity-90" onClick={onInventur}>Jetzt zählen</button>
      </section>
    );
  }
  const n = konto.aktuell;
  const pct = Math.max(0, Math.min(100, (n / max) * 100));
  const ton = n > max ? 'red' : n > max - 20 ? 'amber' : 'green';
  const balken = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-emerald-500' }[ton];
  const alt = (konto.tage_seit_zaehlung ?? 0) > 30;
  return (
    <section data-testid="gas-konto" className="mb-4 bg-white border border-slate-200 rounded-xl px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <div className="min-w-[200px]">
          <div className="text-xs uppercase tracking-wide text-slate-500">Flaschen bei uns (Konto)</div>
          <div className="flex items-baseline gap-1.5">
            <span data-testid="gas-konto-zahl" className={`text-3xl font-bold tabular-nums ${ton === 'red' ? 'text-red-600' : 'text-slate-900'}`}>{n}</span>
            <span className="text-slate-500">/ {max}</span>
            <span className="text-xs text-slate-500 ml-1">{ton === 'red' ? `${n - max} zu viel` : `${max - n} frei`}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 mt-1.5 overflow-hidden w-56 max-w-full"><div className={`h-full ${balken}`} style={{ width: `${pct}%` }} /></div>
        </div>
        <div className="text-sm text-slate-600 min-w-0 flex-1">
          <div>
            Gezählt am <b className="text-slate-800">{datumDE(inv.datum)}</b>{inv.von ? <> von <b className="text-slate-800">{vorname(inv.von)}</b></> : ''}
            <span className="text-slate-400"> · Werk 1: {inv.summe_w1} · Werk 2: {inv.summe_w2} · davon leer: {inv.summe_leer}{inv.quelle === 'bestellung' ? ' · bei einer Bestellung' : ''}</span>
          </div>
          <div className="text-slate-500">
            {konto.bewegungen.length
              ? <>seitdem {konto.bewegungen.length} Lieferung{konto.bewegungen.length === 1 ? '' : 'en'}: <b className="text-emerald-700">+{konto.plus}</b> geliefert · <b className="text-slate-700">−{konto.minus}</b> Leergut abgeholt</>
              : 'seitdem keine bestätigte Lieferung'}
          </div>
          {alt && <div className="text-amber-700 font-medium">⚠ Letzte Zählung ist {konto.tage_seit_zaehlung} Tage alt — bitte neu zählen.</div>}
        </div>
        <button className="btn-secondary" onClick={onInventur} data-testid="gas-inventur-btn">🧮 Inventur (neu zählen)</button>
      </div>
    </section>
  );
}

// Bilanz in der Zusammenfassung: Bestand + bestellt − Leergut = danach
function BilanzKarte({ bil, konto, onLeergut, trotzdem = false, onTrotzdem }: { bil: Bilanz; konto: GasKonto | null; onLeergut: (v: number) => void; trotzdem?: boolean; onTrotzdem?: (v: boolean) => void }) {
  const ueber = bil.nach - bil.max;
  const knapp = ueber <= 0 && bil.max - bil.nach <= 20;
  const ton = !bil.erfasst ? 'amber' : ueber > 0 ? 'red' : knapp ? 'amber' : 'green';
  const farben = { red: 'bg-red-50 border-red-300 text-red-800', amber: 'bg-amber-50 border-amber-300 text-amber-900', green: 'bg-emerald-50 border-emerald-200 text-emerald-900' }[ton];
  const balken = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-emerald-500' }[ton];
  const pct = Math.max(0, Math.min(100, (bil.nach / bil.max) * 100));
  return (
    <div data-testid="gas-bilanz" className={`border rounded-xl p-4 text-sm ${farben}`} aria-live="polite">
      <div className="font-semibold mb-1">Flaschen nach der Lieferung</div>
      {bil.erfasst ? (
        <>
          <div className="flex items-baseline gap-1"><b className="text-2xl tabular-nums">{bil.nach}</b><span>/ {bil.max}</span></div>
          <div className="h-2 rounded-full bg-white/70 my-2 overflow-hidden"><div className={`h-full ${balken}`} style={{ width: `${pct}%` }} /></div>
          <div className="tabular-nums text-xs space-y-0.5">
            <div className="flex justify-between"><span>{bil.aus_konto ? `Flaschenkonto${konto?.inventur ? ` (${datumDE(konto.inventur.datum)})` : ''}` : 'Bestand gezählt'}</span><b>{bil.bestand}</b></div>
            <div className="flex justify-between"><span>+ bestellt</span><b>{bil.bestellt}</b></div>
            <div className="flex justify-between"><span>− Leergut</span><b>{bil.leergut}</b></div>
          </div>
          {ueber > 0 && (
            <div className="mt-2">
              ⛔ <b>{ueber} zu viel</b> – Zusatzrechnung droht. PDF/Outlook gesperrt.
              <button className="btn-secondary !py-1 mt-1.5 w-full" onClick={() => onLeergut(ueber)}>Schweißgas-Leergut um {ueber} erhöhen</button>
            </div>
          )}
          {ueber <= 0 && knapp && <div className="mt-2">⚠ Nur noch {bil.max - bil.nach} Flaschen Luft.</div>}
          {!bil.aus_konto && konto?.aktuell != null && bil.bestand < konto.aktuell * 0.6 && (
            <div className="mt-2 font-semibold" data-testid="gas-teilzaehlung">⚠ Gezählt {bil.bestand}, Konto sagt {konto.aktuell} — wirklich vollständig gezählt? Eine Teilzählung verfälscht das Flaschenkonto.</div>
          )}
          {bil.leer_bei_uns > 0 && bil.leergut > bil.leer_bei_uns && <div className="mt-2 font-semibold">Mehr Leergut angemeldet ({bil.leergut}), als leer gezählt ({bil.leer_bei_uns}).</div>}
        </>
      ) : (
        <div>
          <div>Bitte Bestand eintragen oder im Flaschenkonto eine Inventur machen — ohne Bestand kein Versand.</div>
          {onTrotzdem && (
            <label className="mt-2 flex items-start gap-2 bg-white/70 rounded-lg px-2.5 py-2 cursor-pointer">
              <input type="checkbox" className="mt-0.5" data-testid="gas-trotzdem" checked={trotzdem} onChange={(x) => onTrotzdem(x.target.checked)} />
              <span><b>Ausnahme: ohne Bestand senden.</b> Die 300-Flaschen-Prüfung entfällt; die Bestellung wird als „ohne Bestand“ markiert.</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

// Inventur: alle Flaschen zählen (Werk 1 / Werk 2 / davon leer) — unabhängig von einer Bestellung
function InventurModal({ e, konto, onClose, onSaved }: { e: GasEinstellungen; konto: GasKonto | null; onClose: () => void; onSaved: () => void }) {
  const [datum, setDatum] = useState(heute());
  const [notiz, setNotiz] = useState('');
  const [werte, setWerte] = useState<Record<string, GasBestandWert>>({});
  const [busy, setBusy] = useState(false);
  const letzte = konto?.inventur?.bestand ?? {};
  const artikel = e.artikel.filter((a) => (a.flaschen ?? 1) > 0);
  const gefuellt = Object.fromEntries(Object.entries(werte).map(([k, v]) => [k, Object.fromEntries(Object.entries(v).filter(([, x]) => (x ?? '') !== ''))]).filter(([, v]) => Object.keys(v as object).length)) as Record<string, GasBestandWert>;
  const s = flaschenBilanz(e, gefuellt, [], 0);
  const speichern = async () => {
    setBusy(true);
    try { await api.gas.inventur({ datum, bestand: gefuellt, notiz: notiz || undefined }); onSaved(); }
    catch (err) { alert(fehlerText(err)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={(x) => x.stopPropagation()} data-testid="gas-inventur">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900">🧮 Inventur — Flaschen zählen</h2>
            <div className="text-xs text-slate-500">Alle Flaschen (voll + leer) je Werk. Grau = letzte Zählung{konto?.inventur ? ` vom ${datumDE(konto.inventur.datum)}` : ''}.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-4">
          <div className="flex gap-3 mb-3 flex-wrap">
            <Feld label="Gezählt am"><input type="date" className="input" value={datum} onChange={(x) => setDatum(x.target.value)} /></Feld>
            <div className="flex-1 min-w-[200px]"><Feld label="Notiz (optional)"><input className="input w-full" value={notiz} onChange={(x) => setNotiz(x.target.value)} placeholder="z. B. Monatsinventur" /></Feld></div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr><th className="text-left py-1.5">Artikel</th><th className="w-[70px]">Werk 1</th><th className="w-[70px]">Werk 2</th><th className="w-[70px]">davon leer</th></tr>
            </thead>
            <tbody>
              {artikel.map((a) => {
                const k = schluessel(a);
                return (
                  <tr key={k} className="border-b border-slate-100">
                    <td className="py-1 pr-2"><div className="text-slate-800">{a.artikel}</div><div className="text-[11px] text-slate-400 font-mono">{a.artnr}{a.groesse ? ` · ${a.groesse}` : ''}</div></td>
                    <BestandZellen name={a.artikel} wert={werte[k] ?? {}} letzte={letzte[k]} onChange={(w) => setWerte((x) => ({ ...x, [k]: w }))} />
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 text-sm text-slate-700 tabular-nums" data-testid="gas-inventur-summe">
            Summe: <b className="text-lg">{s.bestand_w1 + s.bestand_w2}</b> Flaschen <span className="text-slate-500">(Werk 1: {s.bestand_w1} · Werk 2: {s.bestand_w2} · davon leer: {s.leer_bei_uns})</span>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50" data-testid="gas-inventur-speichern"
                  disabled={busy || !s.erfasst} onClick={speichern}>{busy ? 'Speichern…' : 'Zählung speichern'}</button>
        </div>
      </div>
    </div>
  );
}

// Lieferung bestätigen: Lieferdatum + tatsächlich abgeholtes Leergut → Flaschenkonto
function LieferungModal({ b, onClose, onSaved }: { b: GasBestellung; onClose: () => void; onSaved: (neu: GasBestellung) => void }) {
  const [datum, setDatum] = useState(heute());
  const [ab, setAb] = useState<LeergutEingabe>(leergutAus(b.leergut_detail, b.leergut));
  const [busy, setBusy] = useState(false);
  const plus = b.positionen.reduce((s, p) => s + zahl(p.menge), 0);
  const speichern = async () => {
    setBusy(true);
    try { const r = await api.gas.update(b.id, { status: 'geliefert', geliefert_am: datum, leergut_abgeholt: leergutZahlen(ab) } as Partial<GasBestellung>); onSaved(r.data); }
    catch (err) { alert(fehlerText(err)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-16" onClick={(x) => x.stopPropagation()} data-testid="gas-lieferung">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">✓ Lieferung bestätigen</h2>
          <div className="text-xs text-slate-500">Bestellung vom {datumDE(b.bestelldatum)}: {b.positionen.filter((p) => hatMenge(p.menge)).map((p) => `${p.menge}× ${p.artikel}`).join(', ')}</div>
        </div>
        <div className="px-6 py-4 space-y-3">
          <Feld label="Geliefert am"><input type="date" className="input" value={datum} onChange={(x) => setDatum(x.target.value)} /></Feld>
          <div>
            <div className="text-xs text-slate-500 mb-1">Leergut tatsächlich abgeholt (vorbelegt mit der Bestellung)</div>
            <div className="grid grid-cols-4 gap-2">
              {LEERGUT_ARTEN.map((a) => (
                <label key={a.k} className="block"><span className="block text-[11px] text-slate-500">{a.label}</span>
                  <input className="input w-full text-right" inputMode="numeric" aria-label={`Abgeholt ${a.label}`} value={ab[a.k] ?? ''}
                         onChange={(x) => setAb((v) => ({ ...v, [a.k]: x.target.value.replace(/[^\d]/g, '') }))} /></label>
              ))}
            </div>
          </div>
          <div className="text-sm bg-slate-50 rounded-lg px-3 py-2 tabular-nums">Flaschenkonto: <b className="text-emerald-700">+{plus}</b> geliefert · <b>−{leergutFlaschen(ab)}</b> Leergut</div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="bg-emerald-600 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:opacity-90" data-testid="gas-lieferung-ok" disabled={busy} onClick={speichern}>{busy ? 'Speichern…' : 'Lieferung bestätigen'}</button>
        </div>
      </div>
    </div>
  );
}

// nur Felder behalten, die der Nutzer schon ausgefüllt hat (Standardwerte nicht überschreiben)
function pickGefuellt(k: Kopf): Partial<Kopf> {
  const o: Partial<Kopf> = {};
  (Object.keys(k) as (keyof Kopf)[]).forEach((f) => { if (k[f] && f !== 'bestelldatum') o[f] = k[f]; });
  return o;
}

// Auswertung: was bestellen wir wie oft, wann zuletzt (nur gesendete Bestellungen)
const ZEITRAEUME = [[3, '3 Monate'], [6, '6 Monate'], [12, '12 Monate'], [0, 'Alle']] as const;

function Auswertung({ e }: { e: GasEinstellungen }) {
  const [monate, setMonate] = useState<number>(12);
  const st = useApi(() => api.gas.statistik(monate), [monate]);
  const d: GasStatistik | null = st.data?.data ?? null;
  const max = Math.max(1, ...(d?.artikel ?? []).map((a) => a.menge));
  const tageSeit = (iso: string) => Math.round((Date.now() - Date.parse(iso)) / 86400000);
  const flaschenArtikel = new Map(e.artikel.map((a) => [`${a.artnr}|${a.artikel}`, a.flaschen ?? 1]));

  return (
    <section className="mb-6">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-semibold text-slate-800 mr-auto">Auswertung <span className="text-slate-400 font-normal text-sm">· nur gesendete Bestellungen</span></h2>
        <div className="flex gap-1 border border-slate-200 bg-white rounded-lg p-0.5">
          {ZEITRAEUME.map(([m, t]) => (
            <button key={m} onClick={() => setMonate(m)}
                    className={`px-3 py-1 rounded text-xs font-medium ${monate === m ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{t}</button>
          ))}
        </div>
      </div>
      {st.loading && !d ? <div className="text-sm text-slate-500">Lade …</div> : st.error ? <div className="text-sm text-red-600">{fehlerText(st.error)}</div> : !d || !d.bestellungen ? (
        <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-5">
          Im gewählten Zeitraum gibt es noch keine gesendeten Bestellungen. Sobald Bestellungen per Outlook verschickt
          (oder alte Bestellungen übernommen) sind, erscheint hier die Auswertung.
        </div>
      ) : (<>
        {/* Kennzahlen */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Kachel titel="Letzte Bestellung" wert={datumDE(d.letzte?.bestelldatum ?? '')}
                  sub={d.letzte ? `vor ${tageSeit(d.letzte.bestelldatum)} Tagen · ` + d.letzte.positionen.filter((p) => hatMenge(p.menge)).map((p) => `${p.menge}× ${p.artikel}`).join(', ') : ''} />
          <Kachel titel="Bestellungen" wert={String(d.bestellungen)} sub={d.abstand_tage != null ? `im Schnitt alle ${d.abstand_tage} Tage` : ''} />
          <Kachel titel="Flaschen bestellt" wert={String(d.flaschen)} sub={`Ø ${Math.round(d.flaschen / d.bestellungen)} pro Bestellung`} />
          <Kachel titel="Meistbestellt" wert={d.artikel[0]?.artikel ?? '—'} sub={d.artikel[0] ? `${d.artikel[0].menge} Stück in ${d.artikel[0].anzahl} Bestellungen` : ''} klein />
        </div>

        {/* Rangliste je Gas */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Gas / Artikel</th>
                <th className="text-left px-3 py-2 w-[34%]">Menge gesamt</th>
                <th className="text-right px-3 py-2">Bestellungen</th>
                <th className="text-right px-3 py-2">Ø je Bestellung</th>
                <th className="text-left px-3 py-2">Zuletzt bestellt</th>
              </tr>
            </thead>
            <tbody>
              {d.artikel.map((a) => {
                const tank = flaschenArtikel.get(`${a.artnr}|${a.artikel}`) === 0;
                return (
                  <tr key={a.artnr + a.artikel} className="border-b border-slate-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{a.artikel}</div>
                      <div className="text-xs text-slate-400 font-mono">{a.artnr}{a.groesse ? ` · ${a.groesse}` : ''}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2" title={`${a.menge} ${tank ? 'Füllungen' : 'Stück'}`}>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand rounded-full" style={{ width: `${(a.menge / max) * 100}%` }} />
                        </div>
                        <span className="tabular-nums font-semibold text-slate-800 w-10 text-right">{a.menge}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{a.anzahl}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{(a.menge / a.anzahl).toLocaleString('de-DE', { maximumFractionDigits: 1 })}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {datumDE(a.zuletzt)} <span className="text-slate-400">· {a.zuletzt_menge}×</span>
                      <div className="text-xs text-slate-400">vor {tageSeit(a.zuletzt)} Tagen</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Monatsverlauf daten={d.monatsverlauf} />
      </>)}
    </section>
  );
}

function Kachel({ titel, wert, sub, klein }: { titel: string; wert: string; sub?: string; klein?: boolean }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-0">
      <div className="text-xs uppercase tracking-wide text-slate-500">{titel}</div>
      <div className={`font-bold text-slate-900 tabular-nums truncate ${klein ? 'text-base mt-1' : 'text-2xl'}`} title={wert}>{wert}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2" title={sub}>{sub}</div>}
    </div>
  );
}

// Flaschen je Monat (Säulen, eine Serie). Lücken-Monate werden mit 0 aufgefüllt.
function Monatsverlauf({ daten }: { daten: GasStatistik['monatsverlauf'] }) {
  const [hover, setHover] = useState<number | null>(null);
  if (!daten.length) return null;
  const map = new Map(daten.map((m) => [m.monat, m]));
  const monate: { monat: string; flaschen: number; bestellungen: number }[] = [];
  const [y0, m0] = daten[0]!.monat.split('-').map(Number) as [number, number];
  const jetzt = new Date();
  for (let y = y0, m = m0; y < jetzt.getFullYear() || (y === jetzt.getFullYear() && m <= jetzt.getMonth() + 1); m === 12 ? (y++, m = 1) : m++) {
    const k = `${y}-${String(m).padStart(2, '0')}`;
    monate.push(map.get(k) ?? { monat: k, flaschen: 0, bestellungen: 0 });
  }
  const max = Math.max(1, ...monate.map((m) => m.flaschen));
  const iMax = monate.findIndex((m) => m.flaschen === max);
  const name = (k: string) => new Date(k + '-01').toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
  const jede = monate.length > 12 ? 3 : 1;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-sm font-semibold text-slate-800 mb-3">Flaschen pro Monat</div>
      <div className="relative">
        <div className="flex items-end gap-[2px] h-36 border-b border-slate-200" onMouseLeave={() => setHover(null)}>
          {monate.map((m, i) => (
            <div key={m.monat} className="flex-1 h-full flex flex-col justify-end items-center relative cursor-default"
                 onMouseEnter={() => setHover(i)}>
              {i === iMax && hover === null && m.flaschen > 0 && <div className="text-[11px] text-slate-600 tabular-nums mb-0.5">{m.flaschen}</div>}
              <div className={`w-full max-w-[36px] rounded-t ${hover === i ? 'bg-brand-dark' : 'bg-brand'}`}
                   style={{ height: `${(m.flaschen / max) * 100}%`, minHeight: m.flaschen ? 2 : 0 }} />
            </div>
          ))}
        </div>
        {hover !== null && monate[hover] && (
          <div className="absolute -top-2 z-10 bg-slate-900 text-white text-xs rounded-md px-2 py-1 pointer-events-none whitespace-nowrap"
               style={{ left: `${((hover + 0.5) / monate.length) * 100}%`, transform: 'translate(-50%, -100%)' }}>
            <b>{name(monate[hover].monat)}</b> · {monate[hover].flaschen} Flaschen · {monate[hover].bestellungen} Bestellung{monate[hover].bestellungen === 1 ? '' : 'en'}
          </div>
        )}
        <div className="flex gap-[2px] mt-1">
          {monate.map((m, i) => (
            <div key={m.monat} className="flex-1 text-center text-[10px] text-slate-400 truncate">{i % jede === 0 ? name(m.monat) : ''}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Drei Eingaben je Artikel: Flaschen in Werk 1, Werk 2 und davon leer (beide Werke)
function BestandZellen({ name, wert, letzte, onChange, nurLeer = false }: {
  name: string; wert: GasBestandWert; letzte?: GasBestandWert; onChange: (w: GasBestandWert) => void; nurLeer?: boolean;
}) {
  const nur = (s: string) => s.replace(/[^\d]/g, '');
  const zuViel = !nurLeer && zahl(wert.leer) > zahl(wert.w1) + zahl(wert.w2);
  const feld = (f: keyof GasBestandWert, label: string, erste = false) => (
    <td className={`px-1 py-1 text-right ${erste ? 'border-l border-slate-100' : ''}`}>
      <input className={`input w-14 text-right ${f === 'leer' ? 'bg-slate-50' : ''} ${f === 'leer' && zuViel ? '!border-red-400' : ''}`}
             inputMode="numeric" aria-label={`Bestand ${label} ${name}`} placeholder={letzte?.[f] ?? ''}
             title={f === 'leer' && zuViel ? 'Mehr leere als vorhandene Flaschen' : undefined}
             value={wert[f] ?? ''} onChange={(x) => onChange({ ...wert, [f]: nur(x.target.value) })} />
    </td>
  );
  if (nurLeer) return feld('leer', 'leer', true);
  return <>{feld('w1', 'Werk 1', true)}{feld('w2', 'Werk 2')}{feld('leer', 'leer')}</>;
}

function Feld({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

// Mengenfeld mit −/+ (Flaschen/Bündel sind ganze Stück)
function Menge({ wert, onChange }: { wert: string; onChange: (v: string) => void }) {
  const n = parseInt(wert) || 0;
  return (
    <div className="flex items-center justify-end gap-1">
      <button type="button" className="w-7 h-8 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-30"
              disabled={n <= 0} onClick={() => onChange(n - 1 > 0 ? String(n - 1) : '')} aria-label="weniger">−</button>
      <input className="input w-16 text-right font-semibold" inputMode="numeric" value={wert}
             onChange={(x) => onChange(x.target.value.replace(/[^\d.,]/g, ''))} />
      <button type="button" className="w-7 h-8 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
              onClick={() => onChange(String(n + 1))} aria-label="mehr">＋</button>
    </div>
  );
}

function EinstellungenModal({ wert, onClose, onSaved }: { wert: GasEinstellungen; onClose: () => void; onSaved: () => void }) {
  const [e, setE] = useState<GasEinstellungen>(JSON.parse(JSON.stringify(wert)));
  const [busy, setBusy] = useState(false);
  const lf = (p: Partial<GasEinstellungen['lieferant']>) => setE({ ...e, lieferant: { ...e.lieferant, ...p } });
  const bs = (p: Partial<GasEinstellungen['besteller']>) => setE({ ...e, besteller: { ...e.besteller, ...p } });
  const art = (i: number, p: Partial<GasArtikel>) => setE({ ...e, artikel: e.artikel.map((a, j) => (j === i ? { ...a, ...p } : a)) });
  const verschieben = (i: number, d: number) => {
    const a = [...e.artikel]; const j = i + d;
    if (j < 0 || j >= a.length) return;
    const x = a[i]!; a[i] = a[j]!; a[j] = x; setE({ ...e, artikel: a });
  };
  const speichern = async () => {
    setBusy(true);
    try { await api.gas.einstellungenSpeichern(e); onSaved(); }
    catch (err) { alert(fehlerText(err)); setBusy(false); }
  };
  const feld = (label: string, v: string, f: (s: string) => void) => (
    <Feld label={label}><input className="input w-full" value={v} onChange={(x) => f(x.target.value)} /></Feld>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(x) => x.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">⚙ Gaslieferant, Standardwerte &amp; Artikelliste</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Lieferant</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {feld('Name', e.lieferant.name, (v) => lf({ name: v }))}
              {feld('Abteilung', e.lieferant.abteilung, (v) => lf({ abteilung: v }))}
              {feld('Unsere Kunden-Nr.', e.lieferant.kundennr, (v) => lf({ kundennr: v }))}
              {feld('Straße', e.lieferant.strasse, (v) => lf({ strasse: v }))}
              {feld('PLZ Ort', e.lieferant.ort, (v) => lf({ ort: v }))}
              {feld('Bestell-E-Mail', e.lieferant.email, (v) => lf({ email: v }))}
              {feld('Telefon', e.lieferant.telefon, (v) => lf({ telefon: v }))}
              {feld('Telefax', e.lieferant.telefax, (v) => lf({ telefax: v }))}
              {feld('Lieferturnus', e.lieferant.lieferturnus, (v) => lf({ lieferturnus: v }))}
              {feld('Vertrag: max. Flaschen bei uns', String(e.vertrag?.maxFlaschen ?? 300), (v) => setE({ ...e, vertrag: { maxFlaschen: parseInt(v.replace(/\D/g, '')) || 0 } }))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Standardwerte für neue Bestellungen</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {feld('Ansprechpartner', e.besteller.ansprechpartner, (v) => bs({ ansprechpartner: v }))}
              {feld('E-Mail', e.besteller.email, (v) => bs({ email: v }))}
              {feld('Telefon', e.besteller.telefon, (v) => bs({ telefon: v }))}
              {feld('Lieferadresse', e.besteller.lieferadresse, (v) => bs({ lieferadresse: v }))}
              {feld('Rechnungsadresse', e.besteller.rechnungsadresse, (v) => bs({ rechnungsadresse: v }))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Artikelliste (Reihenfolge wie im Formular) · Zahl: zählt als … Flaschen · „Zusatz“: nicht im Formular, im PDF als freie Zeile</div>
            <div className="space-y-1.5">
              {e.artikel.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className="input w-28 font-mono" placeholder="Art.-Nr" value={a.artnr} onChange={(x) => art(i, { artnr: x.target.value })} />
                  <input className="input flex-1" placeholder="Artikel" value={a.artikel} onChange={(x) => art(i, { artikel: x.target.value })} />
                  <input className="input w-28" placeholder="Größe/Typ" value={a.groesse} onChange={(x) => art(i, { groesse: x.target.value })} />
                  <input className="input w-14 text-right" title="Zählt als … Flaschen (Tank = 0, Bündel = Anzahl Flaschen)" value={String(a.flaschen ?? 1)}
                         onChange={(x) => art(i, { flaschen: parseInt(x.target.value.replace(/\D/g, '')) || 0 })} />
                  <label className="flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap" title="Zusatzartikel: in der App gelistet, im PDF nur bei Bestellung in einer freien Zeile (Formular bleibt unverändert)">
                    <input type="checkbox" checked={!!a.zusatz} onChange={(x) => art(i, { zusatz: x.target.checked })} /> Zusatz
                  </label>
                  <button className="text-slate-400 hover:text-slate-700 px-1" onClick={() => verschieben(i, -1)} title="nach oben">↑</button>
                  <button className="text-slate-400 hover:text-slate-700 px-1" onClick={() => verschieben(i, 1)} title="nach unten">↓</button>
                  <button className="text-slate-300 hover:text-red-500 px-1" onClick={() => setE({ ...e, artikel: e.artikel.filter((_, j) => j !== i) })} title="entfernen">×</button>
                </div>
              ))}
              <button className="text-sm text-brand hover:underline" onClick={() => setE({ ...e, artikel: [...e.artikel, { artnr: '', artikel: '', groesse: '', flaschen: 1 }] })}>＋ Artikel</button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90" disabled={busy} onClick={speichern}>
            {busy ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
